/**
 * Dashboard Store
 *
 * Manages dashboard UI state including selected project, view mode.
 * Persisted so the selected project survives page refresh.
 *
 * @module store/dashboard/dashboardStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { createPersistConfig } from '../middleware/persistenceMiddleware';

export const useDashboardStore = create(
  persist(
    (set) => ({
      // State
      selectedDashboardProject: '',
      dashboardMode: 'general',
      projectDisplayName: '',
      activeTab: 'overview',
      fromDate: null,
      toDate: null,
      resultLimit: 20,

      // Actions

      updateDashboardProject: (projectId, displayName = '') => {
        logger.debug('Updating dashboard project', { projectId, displayName });
        set({
          selectedDashboardProject: projectId,
          projectDisplayName: displayName,
          dashboardMode: 'project-specific'
        });
      },

      resetToGeneralDashboard: () => {
        logger.debug('Resetting to general dashboard');
        set({ dashboardMode: 'general', projectDisplayName: '' });
      },

      updateProjectOnly: (projectId) => {
        logger.debug('Updating project only', { projectId });
        set({ selectedDashboardProject: projectId });
      },

      setActiveTab: (tab) => {
        set({ activeTab: tab });
      },

      setDateRange: (fromDate, toDate) => {
        logger.debug('Updating date range', { fromDate, toDate });
        set({ fromDate, toDate });
      },

      setResultLimit: (limit) => {
        set({ resultLimit: limit });
      }
    }),
    createPersistConfig('dashboardState', {
      partialize: state => ({
        selectedDashboardProject: state.selectedDashboardProject,
        activeTab: state.activeTab,
        resultLimit: state.resultLimit
      })
    })
  )
);

// Granular selector hooks

export const useSelectedDashboardProject = () =>
  useDashboardStore(state => state.selectedDashboardProject);

export const useDashboardMode = () =>
  useDashboardStore(state => state.dashboardMode);

export const useProjectDisplayName = () =>
  useDashboardStore(state => state.projectDisplayName);

export const useActiveTab = () =>
  useDashboardStore(state => state.activeTab);

export const useFromDate = () =>
  useDashboardStore(state => state.fromDate);

export const useToDate = () =>
  useDashboardStore(state => state.toDate);

export const useResultLimit = () =>
  useDashboardStore(state => state.resultLimit);

export const useDashboardActions = () =>
  useDashboardStore(
    useShallow(state => ({
      updateDashboardProject: state.updateDashboardProject,
      resetToGeneralDashboard: state.resetToGeneralDashboard,
      updateProjectOnly: state.updateProjectOnly,
      setActiveTab: state.setActiveTab,
      setDateRange: state.setDateRange,
      setResultLimit: state.setResultLimit
    }))
  );
